import { useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { coerceRichTextValue } from "../utils/richTextValue";

const RichTextEdit = ({
  name,
  value,
  onChange,
  readOnly,
  instanceKey,
}: {
  name: string;
  value: unknown;
  onChange: (payload: { name: string; value: string }) => void;
  readOnly?: boolean;
  /** Remount Quill when the edited item/field changes so HTML is parsed on load. */
  instanceKey?: string;
}) => {
  const normalizedValue = useMemo(() => coerceRichTextValue(value), [value]);
  const editorRef = useRef<ReactQuill | null>(null);
  const lastEmittedValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastEmittedValueRef.current === normalizedValue) {
      lastEmittedValueRef.current = null;
      return;
    }
    lastEmittedValueRef.current = null;

    const editor = editorRef.current?.getEditor();
    if (!editor || coerceRichTextValue(editor.root.innerHTML) === normalizedValue) {
      return;
    }

    const selection = editor.getSelection();
    editor.setContents(editor.clipboard.convert(normalizedValue), "silent");
    if (selection) {
      const maximumIndex = Math.max(0, editor.getLength() - 1);
      editor.setSelection(
        Math.min(selection.index, maximumIndex),
        selection.length,
        "silent"
      );
    }
  }, [normalizedValue, instanceKey]);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "code-block", "link"],
        ["clean"],
      ],
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
      },
    }),
    []
  );

  const formats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "color",
      "background",
      "list",
      "bullet",
      "align",
      "blockquote",
      "code-block",
      "link",
    ],
    []
  );

  const handleChange = (nextValue: string) => {
    lastEmittedValueRef.current = coerceRichTextValue(nextValue);
    onChange({ name, value: nextValue });
  };

  return (
    <div className="bg-white">
      <ReactQuill
        ref={editorRef}
        key={instanceKey || name}
        theme="snow"
        defaultValue={normalizedValue}
        onChange={handleChange}
        modules={readOnly ? { toolbar: false } : modules}
        formats={formats}
        readOnly={readOnly}
        className="w-full"
      />
    </div>
  );
};

export default RichTextEdit;
